import { inject, provide, InjectionKey } from "vue";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

export type ContactAttachOption = {
	Id: number,
	Name: string
}

/**
 * Контекст блока контактов для текущей страницы.
 *
 * Общие компоненты контактов (ClientsPanelListItem, CreateEditContactDropdown,
 * ContactAddClientDropdown) используются на Главной, Клиенте и Проекте. Мутации
 * существующего контакта (теги, привязка клиента, редактирование) патчат сам
 * объект по ссылке и реактивны на всех страницах. А операции, меняющие СОСТАВ
 * списка (создание/удаление), завязаны на конкретную страницу — для них и нужен
 * этот контекст.
 */
export type ContactsContext = {
	context: "main" | "client" | "project",
	/** Id текущего проекта (страница Проекта) — для статуса главного контакта. */
	readonly projectId?: number | null,
	/** Id клиента, к которому привязывается новый контакт (страница Клиента). */
	readonly attachClientId?: number | null,
	/** Клиенты проекта для селекта привязки в окне создания (страница Проекта). */
	readonly attachClientOptions?: ContactAttachOption[],
	/** Адрес, подставляемый в окно создания контакта (страница Клиента). */
	readonly defaultAddress?: RequestAddressContainer | null,
	/** Перезапрос текущего отфильтрованного списка контактов страницы. */
	refetchContacts?: () => Promise<void> | void,
	/**
	 * Показать в списке страницы уже существующие контакты, найденные проверкой дублей.
	 * contactText — введённые контактные данные, по ним подсвечиваются совпадения в списке.
	 */
	showExistContacts?: (contactIds: number[], contactText: string) => Promise<void> | void
}

const CONTACTS_CONTEXT: InjectionKey<ContactsContext> = Symbol("contactsContext");

export const provideContactsContext = (value: ContactsContext): void => {
	provide(CONTACTS_CONTEXT, value);
};

export const useContactsContext = (): ContactsContext => {
	return inject(CONTACTS_CONTEXT, { context: "main" });
};
